import { getTile } from '../../map/services/offlineMapService'

/**
 * Tile image loader for the static site-map compositor. Separate module so
 * unit tests can mock network tile loading (vi.mock) without touching the
 * composition logic in staticMapService.
 *
 * 009 (R6): with a key, the offline bundle in IndexedDB is tried first — the
 * same lookup the interactive map's ImageryTileLayer makes — so a report
 * generated in the field draws from the tiles downloaded for that site.
 */

const TILE_TIMEOUT_MS = 12_000

/** Offline-bundle key for a tile: `sourceId/z/x/y` in IndexedDB */
export interface TileKey {
  sourceId: string
  z: number
  x: number
  y: number
}

function loadImage(src: string, onSettled?: () => void): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const timer = setTimeout(() => {
      img.src = ''
      onSettled?.()
      reject(new Error('Tile request timed out'))
    }, TILE_TIMEOUT_MS)
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      clearTimeout(timer)
      onSettled?.()
      resolve(img)
    }
    img.onerror = () => {
      clearTimeout(timer)
      onSettled?.()
      reject(new Error('Tile failed to load'))
    }
    img.src = src
  })
}

export async function loadTile(url: string, key?: TileKey): Promise<HTMLImageElement> {
  if (key) {
    let blob: Blob | null = null
    try {
      blob = (await getTile(key.sourceId, key.z, key.x, key.y))?.blob ?? null
    } catch {
      // IndexedDB unavailable (private mode, blocked storage): use the network
    }
    if (blob) {
      const objectUrl = URL.createObjectURL(blob)
      try {
        return await loadImage(objectUrl, () => URL.revokeObjectURL(objectUrl))
      } catch {
        // A corrupt bundled tile falls through to the network
      }
    }
  }
  return loadImage(url)
}
